import { Autocomplete, TextField } from "@mui/material";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import * as React from "react";

import { useFileIdToTopics } from "@/shared/components/visualization/WorkspaceCtx/hooks";
import {
  MessagePathNode,
  TopicNode,
  pathMatcher as matcher,
  pathParser as parser,
} from "@/shared/domain/topics";
import {
  useVizDispatch,
  type TopicDataPathMapping,
} from "@/shared/state/visualization";
import { updateThreeDEntity } from "@/shared/state/visualization/actions";
import {
  Entity,
  Position,
} from "@/shared/state/visualization/schema/v1/three-d";

import styles from "../EntityConfig.module.css";

import { PositionPreset, presetFor, presets } from "./presets";
import { useWorkspacePositionPresets } from "./useWorkspacePositionPresets";

export function PositionPresetSelector(props: {
  panelId: string;
  entity: Entity;
}) {
  const { panelId, entity } = props;
  const dispatch = useVizDispatch();

  const fileIdToTopics = useFileIdToTopics();

  const prefilled = useWorkspacePositionPresets();
  const [options, currentPreset] = React.useMemo(
    function optionsForPresetsAndCurrentState() {
      const options: PositionPreset[] = [...prefilled];

      function isPositionPreset(preset: PositionPreset) {
        return isEqual(positionFor(preset, fileIdToTopics), entity.position);
      }
      const defaults = Object.values(presets);
      let currentPreset =
        prefilled.find(isPositionPreset) || defaults.find(isPositionPreset);
      // Add the current entity position as a pickable option if it can't be found in the list of options.
      if (!currentPreset) {
        const customPreset = presetFor(entity.position);
        if (customPreset) {
          options.push(customPreset);
        }
        currentPreset = customPreset;
      }

      options.push(...defaults);
      return [options, currentPreset];
    },
    [entity.position, prefilled, fileIdToTopics],
  );

  return (
    <Autocomplete
      freeSolo
      size="small"
      fullWidth
      options={options}
      getOptionLabel={(option) =>
        typeof option === "string"
          ? option
          : `${option.name.title} ${option.name.subtitle}`
      }
      renderOption={(renderOptionProps, option: PositionPreset) => (
        <li {...renderOptionProps}>
          <p className={styles.input}>
            <span className={styles.highlight}>{option.name.title}</span>
            {option.name.formatHint && <i> {`(${option.name.formatHint})`}</i>}
            <br />
            <i>{option.name.subtitle}</i>
          </p>
        </li>
      )}
      isOptionEqualToValue={(option, value) => isEqual(option, value)}
      onChange={(_, value) => {
        if (!!value && typeof value === "object") {
          const presetPosition = positionFor(value, fileIdToTopics);
          dispatch(
            updateThreeDEntity(panelId, entity.id, {
              position: presetPosition,
            }),
          );
        }
      }}
      value={currentPreset ?? ""}
      className={classNames([styles.input, styles.field])}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Preset"
          InputProps={{ ...params.InputProps, className: styles.input }}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
            className: styles.input,
          }}
        />
      )}
    />
  );
}

function positionFor(
  preset: PositionPreset | undefined,
  fileIdToTopics: Record<string, TopicNode[]>,
): Position | undefined {
  if (!preset) {
    return undefined;
  }
  const forwardMessagePath = messagePathNodeFor(
    preset.bindings?.forward,
    fileIdToTopics,
  );
  const rightMessagePath = messagePathNodeFor(
    preset.bindings?.right,
    fileIdToTopics,
  );
  const upMessagePath = messagePathNodeFor(preset.bindings?.up, fileIdToTopics);
  return {
    transformation: preset.transformation,
    basePreset: preset.basePreset,
    forward: forwardMessagePath?.toTopicData() ?? 0,
    forwardScale: preset.scales.forward,
    right: rightMessagePath?.toTopicData() ?? 0,
    rightScale: preset.scales.right,
    up: upMessagePath?.toTopicData() ?? 0,
    upScale: preset.scales.up,
  };
}

function messagePathNodeFor(
  preset: TopicDataPathMapping | undefined,
  fileIdToTopics: Record<string, TopicNode[]>,
): MessagePathNode | undefined {
  const parsed = parser.parse(preset?.path ?? "");

  const matches = matcher.findMatches(
    parsed,
    fileIdToTopics[preset?.fileId ?? ""] ?? [],
  );

  const topic = matches.find((match) => match.isComplete)?.match;
  return topic;
}
